import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Search, LogOut, ArrowLeft, ShieldCheck, FileDown, X, RefreshCw,
  User, Mail, Phone, MapPin, ImageIcon, CheckCircle2, AlertTriangle, Clock,
  ChevronRight, TrendingUp, Award, Trophy,
} from "lucide-react";
import { Toaster } from "@/components/ui/sonner";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogClose,
} from "@/components/ui/dialog";
import { KWLogo } from "@/components/KWLogo";
import { downloadReportPDF } from "@/lib/pdf";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUSES = [
  { key: "pending", label: "Pending", color: "gray", icon: Clock },
  { key: "verified", label: "Verified", color: "teal", icon: CheckCircle2 },
  { key: "needs_attention", label: "Needs Attention", color: "red", icon: AlertTriangle },
];

const statusMeta = (s) => STATUSES.find((x) => x.key === s) || STATUSES[0];

const statusChipClass = (s) => {
  const m = statusMeta(s);
  if (m.color === "teal")
    return "bg-[color:var(--kw-teal)]/12 text-[color:var(--kw-teal-dark)] border-[color:var(--kw-teal)]/30";
  if (m.color === "red")
    return "bg-[#fdecec] text-[color:var(--kw-red-dark)] border-[color:var(--kw-red)]/25";
  return "bg-[color:var(--kw-cream)] text-[color:var(--kw-charcoal-soft)] border-[color:var(--kw-border)]";
};

const formatDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-US", { dateStyle: "medium", timeStyle: "short" });
  } catch {
    return iso;
  }
};

const BONUS_THRESHOLD = 4;

const monthLabel = () =>
  new Date().toLocaleDateString("en-US", { month: "long", year: "numeric" });

const isThisMonth = (iso) => {
  if (!iso) return false;
  try {
    const d = new Date(iso);
    const now = new Date();
    return d.getUTCFullYear() === now.getUTCFullYear() && d.getUTCMonth() === now.getUTCMonth();
  } catch {
    return false;
  }
};

export default function AdminDashboard() {
  const navigate = useNavigate();
  const location = useLocation();
  const [user, setUser] = useState(location.state?.user || null);
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [selected, setSelected] = useState(null);

  // Verify session then load reports
  useEffect(() => {
    const boot = async () => {
      try {
        if (!user) {
          const meResp = await fetch(`${API}/auth/me`, { credentials: "include" });
          if (!meResp.ok) throw new Error("unauthenticated");
          setUser(await meResp.json());
        }
        await loadReports();
      } catch {
        navigate("/admin/login", { replace: true });
      }
    };
    boot();
  }, []);

  const loadReports = async () => {
    setLoading(true);
    try {
      const resp = await fetch(`${API}/admin/reports`, { credentials: "include" });
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const data = await resp.json();
      setReports(data.reports || []);
    } catch (e) {
      toast.error("Couldn't load reports");
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await fetch(`${API}/auth/logout`, { method: "POST", credentials: "include" });
    } catch (err) {
      console.error("Logout request failed:", err?.message || err);
    }
    navigate("/admin/login", { replace: true });
  };

  const updateStatus = async (id, status) => {
    try {
      const resp = await fetch(`${API}/admin/reports/${id}/status`, {
        method: "PATCH",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ status }),
      });
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const data = await resp.json();
      setReports((prev) => prev.map((r) => (r.id === id ? data.report : r)));
      if (selected?.id === id) setSelected(data.report);
      toast.success(`Marked ${statusMeta(status).label}`);
    } catch {
      toast.error("Couldn't update status");
    }
  };

  const counts = useMemo(() => {
    const c = { all: reports.length, pending: 0, verified: 0, needs_attention: 0 };
    for (const r of reports) c[r.status || "pending"] += 1;
    return c;
  }, [reports]);

  // Month-to-date leaderboard aggregated by agent email
  const mtdAgents = useMemo(() => {
    const map = new Map();
    for (const r of reports) {
      if (!isThisMonth(r.created_at)) continue;
      const key = (r.email || r.name || "unknown").toLowerCase();
      if (!map.has(key)) {
        map.set(key, { name: r.name, email: r.email, count: 0, latest: r.created_at });
      }
      const agg = map.get(key);
      agg.count += 1;
      if (r.created_at && (!agg.latest || r.created_at > agg.latest)) agg.latest = r.created_at;
    }
    return Array.from(map.values()).sort(
      (a, b) => b.count - a.count || (b.latest || "").localeCompare(a.latest || "")
    );
  }, [reports]);

  const mtdStats = useMemo(() => {
    const total = mtdAgents.reduce((s, a) => s + a.count, 0);
    const qualified = mtdAgents.filter((a) => a.count >= BONUS_THRESHOLD).length;
    return { total, qualified, agents: mtdAgents.length };
  }, [mtdAgents]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return reports.filter((r) => {
      if (statusFilter !== "all" && (r.status || "pending") !== statusFilter) return false;
      if (!q) return true;
      return [r.name, r.email, r.phone, r.address, r.listing_agent]
        .filter(Boolean)
        .some((v) => v.toLowerCase().includes(q));
    });
  }, [reports, query, statusFilter]);

  return (
    <div className="min-h-screen bg-[color:var(--kw-cream)]">
      <Toaster position="top-right" richColors closeButton />
      <div className="kw-red-bar h-1 w-full" />

      {/* Header */}
      <header className="bg-white border-b border-[color:var(--kw-border)]">
        <div className="max-w-7xl mx-auto px-5 md:px-8 py-4 flex items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <button
              data-testid="back-to-hub"
              onClick={() => navigate("/")}
              className="hidden sm:inline-flex items-center gap-1.5 text-xs font-bold tracking-widest uppercase text-[color:var(--muted-foreground)] hover:text-[color:var(--kw-charcoal)] transition-colors"
            >
              <ArrowLeft size={14} strokeWidth={2.5} />
              Hub
            </button>
            <div className="hidden sm:block h-8 w-px bg-[color:var(--kw-border)]" />
            <KWLogo className="h-10" />
            <div className="hidden md:block h-8 w-px bg-[color:var(--kw-border)]" />
            <div className="hidden md:flex items-center gap-1.5 text-[color:var(--kw-red)]">
              <ShieldCheck size={16} strokeWidth={2.5} />
              <span className="font-black uppercase tracking-widest text-xs">Coach Portal</span>
            </div>
          </div>

          <div className="flex items-center gap-3">
            {user && (
              <div className="hidden md:flex items-center gap-2.5 pr-3 border-r border-[color:var(--kw-border)]">
                {user.picture ? (
                  <img
                    src={user.picture}
                    alt={user.name}
                    className="w-8 h-8 rounded-full border border-[color:var(--kw-border)]"
                  />
                ) : (
                  <div className="w-8 h-8 rounded-full bg-[color:var(--kw-red)] text-white flex items-center justify-center font-bold text-xs">
                    {(user.name || user.email || "?").slice(0, 1).toUpperCase()}
                  </div>
                )}
                <div>
                  <div className="text-xs font-bold text-[color:var(--kw-charcoal)] leading-none">{user.name || user.email}</div>
                  <div className="text-[10px] text-[color:var(--muted-foreground)] mt-0.5">{user.email}</div>
                </div>
              </div>
            )}
            <button
              data-testid="logout-btn"
              onClick={handleLogout}
              className="inline-flex items-center gap-1.5 text-xs font-bold tracking-widest uppercase text-[color:var(--muted-foreground)] hover:text-[color:var(--kw-red)] transition-colors"
            >
              <LogOut size={14} strokeWidth={2.5} />
              Sign Out
            </button>
          </div>
        </div>
      </header>

      {/* Toolbar */}
      <section className="max-w-7xl mx-auto px-5 md:px-8 pt-8">
        <div className="flex items-end justify-between gap-4 mb-6 flex-wrap">
          <div>
            <div className="label-caps mb-1">Coaching · Activity Reports</div>
            <h1 className="font-display text-3xl md:text-4xl font-black uppercase tracking-tight text-[color:var(--kw-charcoal)] leading-none">
              Submitted Reports
            </h1>
          </div>
          <button
            data-testid="refresh-btn"
            onClick={loadReports}
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full border border-[color:var(--kw-border)] text-xs font-bold uppercase tracking-widest text-[color:var(--kw-charcoal-soft)] hover:border-[color:var(--kw-charcoal)] hover:text-[color:var(--kw-charcoal)] transition-colors"
          >
            <RefreshCw size={13} strokeWidth={2.5} className={loading ? "animate-spin" : ""} />
            Refresh
          </button>
        </div>

        <div className="flex flex-col md:flex-row md:items-center gap-3 md:gap-4 mb-6">
          <div className="relative flex-1">
            <Search
              size={18}
              strokeWidth={2.2}
              className="absolute left-5 top-1/2 -translate-y-1/2 text-[color:var(--muted-foreground)]"
            />
            <input
              data-testid="admin-search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search by agent, email, address, listing agent…"
              className="w-full pl-12 pr-4 py-3.5 bg-white rounded-full text-sm font-medium border border-transparent shadow-[0_2px_18px_-8px_rgba(43,42,41,0.15)] focus:outline-none focus:border-[color:var(--kw-red)]/40 focus:ring-4 focus:ring-[color:var(--kw-red)]/10 transition-all"
            />
          </div>
        </div>

        {/* Analytics Card */}
        <AnalyticsCard
          monthLabel={monthLabel()}
          stats={mtdStats}
          agents={mtdAgents}
          threshold={BONUS_THRESHOLD}
        />

        {/* Status filter tabs */}
        <div className="flex items-center gap-6 border-b border-[color:var(--kw-border)] mb-6 overflow-x-auto">
          {[
            { key: "all", label: "All", count: counts.all },
            ...STATUSES.map((s) => ({ key: s.key, label: s.label, count: counts[s.key] })),
          ].map((t) => (
            <button
              key={t.key}
              data-testid={`filter-${t.key}`}
              onClick={() => setStatusFilter(t.key)}
              className={`relative pb-3 text-xs font-black tracking-widest uppercase transition-colors whitespace-nowrap ${
                statusFilter === t.key
                  ? "text-[color:var(--kw-red)]"
                  : "text-[color:var(--muted-foreground)] hover:text-[color:var(--kw-charcoal)]"
              }`}
            >
              {t.label}
              <span className="text-[10px] font-bold opacity-70 ml-1.5">({t.count})</span>
              {statusFilter === t.key && (
                <span className="absolute left-0 right-0 -bottom-px h-[3px] bg-[color:var(--kw-red)] rounded-full" />
              )}
            </button>
          ))}
        </div>
      </section>

      {/* Table */}
      <main className="max-w-7xl mx-auto px-5 md:px-8 pb-16">
        {loading ? (
          <div className="text-center py-20 text-[color:var(--muted-foreground)] text-sm">
            Loading reports…
          </div>
        ) : filtered.length === 0 ? (
          <div
            data-testid="empty-state"
            className="text-center py-20 border-2 border-dashed border-[color:var(--kw-border)] rounded-2xl bg-white/60"
          >
            <h3 className="font-display text-xl font-black uppercase text-[color:var(--kw-charcoal)]">
              No reports found
            </h3>
            <p className="text-[color:var(--muted-foreground)] text-sm mt-2">
              {query ? "Try a different search term." : "Once agents submit their open house activity, they'll appear here."}
            </p>
          </div>
        ) : (
          <div className="kw-card overflow-hidden p-0">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-[color:var(--kw-charcoal)] text-white">
                  <tr>
                    <Th>Agent</Th>
                    <Th>Property</Th>
                    <Th>Submitted</Th>
                    <Th>Status</Th>
                    <Th className="text-right pr-6">Action</Th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((r, i) => (
                    <tr
                      key={r.id}
                      data-testid={`row-${r.id}`}
                      onClick={() => setSelected(r)}
                      className={`cursor-pointer transition-colors ${
                        i % 2 === 0 ? "bg-white" : "bg-[color:var(--kw-cream)]/40"
                      } hover:bg-[color:var(--kw-red)]/[0.03]`}
                    >
                      <Td>
                        <div className="font-bold text-[color:var(--kw-charcoal)]">{r.name}</div>
                        <div className="text-xs text-[color:var(--muted-foreground)] mt-0.5">{r.email}</div>
                      </Td>
                      <Td>
                        <div className="text-sm text-[color:var(--kw-charcoal)] line-clamp-1">{r.address}</div>
                        {r.listing_agent && (
                          <div className="text-xs text-[color:var(--muted-foreground)] mt-0.5">Listing: {r.listing_agent}</div>
                        )}
                      </Td>
                      <Td className="text-sm text-[color:var(--kw-charcoal-soft)]">{formatDate(r.created_at)}</Td>
                      <Td>
                        <StatusChip status={r.status || "pending"} />
                      </Td>
                      <Td className="text-right pr-6">
                        <ChevronRight size={16} className="inline text-[color:var(--muted-foreground)]" />
                      </Td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </main>

      <ReportDetailDialog
        report={selected}
        onClose={() => setSelected(null)}
        onStatusChange={updateStatus}
      />
    </div>
  );
}

const Th = ({ children, className = "" }) => (
  <th className={`text-left px-6 py-3 text-[11px] font-bold tracking-widest uppercase ${className}`}>
    {children}
  </th>
);

// ---- Analytics Card ----
const AnalyticsCard = ({ monthLabel, stats, agents, threshold }) => {
  const [expanded, setExpanded] = useState(false);
  const topAgents = expanded ? agents : agents.slice(0, 5);
  const maxCount = Math.max(threshold, ...agents.map((a) => a.count), 1);

  return (
    <section
      data-testid="analytics-card"
      className="kw-card p-0 mb-6 overflow-hidden"
    >
      {/* Header strip */}
      <div className="flex items-center justify-between gap-4 px-6 py-5 border-b border-[color:var(--kw-border)]">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-full bg-[color:var(--kw-red)]/10 flex items-center justify-center">
            <TrendingUp size={16} className="text-[color:var(--kw-red)]" strokeWidth={2.5} />
          </div>
          <div>
            <div className="label-caps mb-0.5">Month-to-Date · {monthLabel}</div>
            <div className="font-display text-xl font-black uppercase tracking-tight text-[color:var(--kw-charcoal)] leading-none">
              Agent Leaderboard
            </div>
          </div>
        </div>
        <div className="text-right">
          <div className="text-[10px] font-bold tracking-widest uppercase text-[color:var(--muted-foreground)]">
            Bonus Threshold
          </div>
          <div className="font-display text-lg font-black text-[color:var(--kw-charcoal)] leading-tight">
            {threshold}+ opens
          </div>
        </div>
      </div>

      {/* KPI row */}
      <div className="grid grid-cols-3 divide-x divide-[color:var(--kw-border)] bg-[color:var(--kw-cream)]/50">
        <Kpi label="Reports Filed" value={stats.total} />
        <Kpi label="Active Agents" value={stats.agents} />
        <Kpi
          label="Qualified for Bonus"
          value={stats.qualified}
          accent="teal"
          icon={Award}
        />
      </div>

      {/* Leaderboard */}
      <div className="px-6 py-5">
        {agents.length === 0 ? (
          <div
            data-testid="analytics-empty"
            className="text-center py-8 text-sm text-[color:var(--muted-foreground)]"
          >
            No reports filed yet this month. When agents submit, their progress will show up here.
          </div>
        ) : (
          <ol className="space-y-3">
            {topAgents.map((a, i) => {
              const qualified = a.count >= threshold;
              const pct = Math.min(100, Math.round((a.count / maxCount) * 100));
              return (
                <li
                  key={a.email || a.name || i}
                  data-testid={`leaderboard-row-${i}`}
                  className="flex items-center gap-4"
                >
                  <div className="w-7 shrink-0 text-center">
                    {i === 0 ? (
                      <Trophy size={18} className="mx-auto text-[color:var(--kw-red)]" strokeWidth={2.5} />
                    ) : (
                      <span className="font-display text-lg font-black text-[color:var(--muted-foreground)]">
                        {i + 1}
                      </span>
                    )}
                  </div>

                  <div className="flex-1 min-w-0">
                    <div className="flex items-baseline justify-between gap-3 mb-1.5">
                      <div className="min-w-0 flex-1">
                        <div className="font-bold text-[color:var(--kw-charcoal)] text-sm truncate">
                          {a.name}
                        </div>
                        <div className="text-[11px] text-[color:var(--muted-foreground)] truncate">
                          {a.email}
                        </div>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <span className="font-display text-base font-black text-[color:var(--kw-charcoal)] tabular-nums">
                          {a.count}
                          <span className="text-xs text-[color:var(--muted-foreground)] font-bold"> / {threshold}</span>
                        </span>
                        {qualified && (
                          <span
                            data-testid={`qualified-${i}`}
                            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-black tracking-widest uppercase bg-[color:var(--kw-teal)]/12 text-[color:var(--kw-teal-dark)] border border-[color:var(--kw-teal)]/30"
                          >
                            <Award size={10} strokeWidth={2.5} />
                            Qualified
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="h-2 rounded-full bg-[color:var(--kw-border)]/60 overflow-hidden">
                      <div
                        className="h-full rounded-full transition-all"
                        style={{
                          width: `${pct}%`,
                          background: qualified
                            ? "linear-gradient(90deg, var(--kw-teal) 0%, var(--kw-teal-dark) 100%)"
                            : "linear-gradient(90deg, var(--kw-red-dark) 0%, var(--kw-red) 100%)",
                        }}
                      />
                    </div>
                  </div>
                </li>
              );
            })}
          </ol>
        )}

        {agents.length > 5 && (
          <button
            data-testid="leaderboard-toggle"
            onClick={() => setExpanded((v) => !v)}
            className="mt-4 text-xs font-black tracking-widest uppercase text-[color:var(--kw-red)] hover:text-[color:var(--kw-red-dark)] transition-colors"
          >
            {expanded ? "Show top 5" : `Show all ${agents.length} agents`}
          </button>
        )}
      </div>
    </section>
  );
};

const Kpi = ({ label, value, accent, icon: Icon }) => (
  <div className="px-5 py-4 flex items-center gap-3">
    {Icon && (
      <div
        className={`w-8 h-8 rounded-full flex items-center justify-center ${
          accent === "teal"
            ? "bg-[color:var(--kw-teal)]/12"
            : "bg-[color:var(--kw-red)]/10"
        }`}
      >
        <Icon
          size={14}
          strokeWidth={2.5}
          className={accent === "teal" ? "text-[color:var(--kw-teal-dark)]" : "text-[color:var(--kw-red)]"}
        />
      </div>
    )}
    <div>
      <div className="label-caps mb-0.5">{label}</div>
      <div
        className={`font-display text-2xl font-black leading-none tabular-nums ${
          accent === "teal" ? "text-[color:var(--kw-teal-dark)]" : "text-[color:var(--kw-charcoal)]"
        }`}
      >
        {value}
      </div>
    </div>
  </div>
);

const Td = ({ children, className = "" }) => (
  <td className={`px-6 py-4 border-t border-[color:var(--kw-border)] align-top ${className}`}>
    {children}
  </td>
);

const StatusChip = ({ status }) => {
  const m = statusMeta(status);
  const Icon = m.icon;
  return (
    <span
      data-testid={`status-chip-${status}`}
      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-black tracking-widest uppercase border ${statusChipClass(status)}`}
    >
      <Icon size={11} strokeWidth={2.5} />
      {m.label}
    </span>
  );
};

// ---- Detail dialog ----
const ReportDetailDialog = ({ report, onClose, onStatusChange }) => {
  const [downloading, setDownloading] = useState(false);
  if (!report) return null;

  const handleDownload = async () => {
    setDownloading(true);
    try {
      await downloadReportPDF(report);
    } catch (e) {
      toast.error("Could not generate PDF");
    } finally {
      setDownloading(false);
    }
  };

  return (
    <Dialog open={!!report} onOpenChange={(v) => !v && onClose()}>
      <DialogContent
        data-testid="report-detail"
        className="max-w-4xl p-0 gap-0 max-h-[92vh] overflow-hidden bg-white border-0 rounded-3xl shadow-2xl [&>button]:hidden"
      >
        <div className="kw-red-bar h-1 w-full" />

        <DialogHeader className="px-8 py-5 border-b border-[color:var(--kw-border)] flex-row items-center justify-between space-y-0">
          <div className="min-w-0 flex-1">
            <div className="text-[11px] font-bold uppercase tracking-[0.18em] text-[color:var(--kw-red)] mb-1">
              Activity Report
            </div>
            <DialogTitle className="font-display text-2xl md:text-3xl font-black tracking-tight text-[color:var(--kw-charcoal)] uppercase truncate">
              {report.name}
            </DialogTitle>
            <div className="text-xs text-[color:var(--muted-foreground)] mt-1">
              Submitted {formatDate(report.created_at)} · ID {report.id?.slice(0, 8)}
            </div>
          </div>
          <DialogClose
            data-testid="report-detail-close"
            className="text-[color:var(--muted-foreground)] hover:text-[color:var(--kw-charcoal)]"
          >
            <X size={22} strokeWidth={2.5} />
          </DialogClose>
        </DialogHeader>

        <div className="overflow-y-auto kw-scroll px-8 py-6 space-y-6" style={{ maxHeight: "calc(92vh - 88px)" }}>
          {/* Status toggle */}
          <div>
            <div className="label-caps mb-2">Review Status</div>
            <div className="flex flex-wrap gap-2">
              {STATUSES.map((s) => {
                const active = (report.status || "pending") === s.key;
                const Icon = s.icon;
                return (
                  <button
                    key={s.key}
                    data-testid={`set-status-${s.key}`}
                    onClick={() => onStatusChange(report.id, s.key)}
                    className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-xs font-black tracking-widest uppercase border-2 transition-all ${
                      active
                        ? s.color === "teal"
                          ? "bg-[color:var(--kw-teal)] text-white border-[color:var(--kw-teal)]"
                          : s.color === "red"
                          ? "bg-[color:var(--kw-red)] text-white border-[color:var(--kw-red)]"
                          : "bg-[color:var(--kw-charcoal)] text-white border-[color:var(--kw-charcoal)]"
                        : "bg-white text-[color:var(--kw-charcoal-soft)] border-[color:var(--kw-border)] hover:border-[color:var(--kw-charcoal)]"
                    }`}
                  >
                    <Icon size={13} strokeWidth={2.5} />
                    {s.label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Info grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <InfoLine icon={User} label="Agent Name" value={report.name} />
            <InfoLine icon={Mail} label="Email" value={report.email} href={`mailto:${report.email}`} />
            <InfoLine icon={Phone} label="Phone" value={report.phone} href={`tel:${report.phone}`} />
            <InfoLine icon={User} label="Listing Agent" value={report.listing_agent || "Not provided"} />
            <InfoLine
              icon={MapPin}
              label="Open House Address"
              value={report.address}
              wide
            />
          </div>

          {/* Yes/No answers */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <YesNoCard
              label="Contacted listing agent prior?"
              value={report.contacted_listing_agent_prior}
            />
            <YesNoCard
              label="Enrolled in Agent Success Program?"
              value={report.enrolled_in_program}
            />
          </div>

          {/* Images */}
          <div>
            <div className="label-caps mb-3 flex items-center gap-1.5">
              <ImageIcon size={12} />
              Uploaded Photos
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <PhotoTile label="Sign In Sheet" image={report.sign_in_sheet} />
              <PhotoTile label="Social Media Screenshot" image={report.social_media} />
            </div>
          </div>

          {/* Download PDF button */}
          <div className="pt-2 pb-1">
            <button
              data-testid="download-pdf-btn"
              onClick={handleDownload}
              disabled={downloading}
              className={`w-full py-4 rounded-full text-base kw-btn-primary inline-flex items-center justify-center gap-2 ${downloading ? "opacity-60 cursor-wait" : ""}`}
            >
              <FileDown size={18} strokeWidth={2.5} />
              {downloading ? "Generating PDF…" : "Download PDF Summary"}
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const InfoLine = ({ icon: Icon, label, value, href, wide = false }) => (
  <div className={wide ? "md:col-span-2" : ""}>
    <div className="label-caps mb-1 flex items-center gap-1.5">
      <Icon size={11} strokeWidth={2.5} className="text-[color:var(--kw-red)]" />
      {label}
    </div>
    {href ? (
      <a href={href} className="text-sm font-bold text-[color:var(--kw-charcoal)] hover:text-[color:var(--kw-red)] transition-colors break-words">
        {value || "—"}
      </a>
    ) : (
      <div className="text-sm font-bold text-[color:var(--kw-charcoal)] break-words">{value || "—"}</div>
    )}
  </div>
);

const YesNoCard = ({ label, value }) => {
  const norm = (value || "").toLowerCase();
  const isYes = norm === "yes";
  const isNo = norm === "no";
  return (
    <div className="rounded-2xl border border-[color:var(--kw-border)] bg-white p-4">
      <div className="label-caps mb-2">{label}</div>
      <div className="flex items-center gap-2">
        <span
          className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-black tracking-widest uppercase border ${
            isYes
              ? "bg-[color:var(--kw-teal)]/12 text-[color:var(--kw-teal-dark)] border-[color:var(--kw-teal)]/30"
              : isNo
              ? "bg-[#fdecec] text-[color:var(--kw-red-dark)] border-[color:var(--kw-red)]/25"
              : "bg-[color:var(--kw-cream)] text-[color:var(--muted-foreground)] border-[color:var(--kw-border)]"
          }`}
        >
          {isYes ? "Yes" : isNo ? "No" : "Not answered"}
        </span>
      </div>
    </div>
  );
};

const PhotoTile = ({ label, image }) => {
  if (!image?.base64) {
    return (
      <div className="rounded-2xl border-2 border-dashed border-[color:var(--kw-border)] bg-white/60 p-6 text-center">
        <div className="label-caps mb-1">{label}</div>
        <div className="text-xs text-[color:var(--muted-foreground)]">Not uploaded</div>
      </div>
    );
  }
  const dataUrl = `data:image/jpeg;base64,${image.base64}`;
  return (
    <div className="rounded-2xl overflow-hidden border border-[color:var(--kw-border)] bg-white">
      <a href={dataUrl} target="_blank" rel="noopener noreferrer" className="block">
        <img src={dataUrl} alt={label} className="w-full h-56 object-cover" />
      </a>
      <div className="p-3">
        <div className="label-caps">{label}</div>
        <div className="text-xs text-[color:var(--muted-foreground)] mt-0.5 truncate">{image.filename}</div>
      </div>
    </div>
  );
};
